// src/components/inputs/DateRangePicker.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    TextField,
    Paper,
    Typography,
    IconButton,
    Stack,
    Grid,
    useTheme,
    alpha,
    Fade,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    CalendarMonth as CalendarIcon,
    Close as CloseIcon,
    SwapHoriz as SwapIcon,
} from '@mui/icons-material';

const DateRangePicker = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    error,
    helperText,
    required = false,
    disabled = false,
    isArabic = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isOpen, setIsOpen] = useState(false);
    const [activeInput, setActiveInput] = useState(null); // 'start' or 'end'
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [hoverDate, setHoverDate] = useState(null);
    const calendarRef = useRef(null);

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setIsOpen(false);
                setActiveInput(null);
                setHoverDate(null);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // ✅ إصلاح: تحويل التاريخ بدون timezone issues
    const formatDateToYYYYMMDD = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Calculate duration - يسمح بنفس اليوم (يوم واحد بدون ليالي)
    const calculateDuration = (start, end) => {
        if (!start || !end) return { days: 0, nights: 0 };
        const startTime = new Date(start).getTime();
        const endTime = new Date(end).getTime();
        const diffTime = Math.abs(endTime - startTime);
        // إذا كان نفس اليوم (diffTime = 0) يعطي يوم واحد بدون ليالي
        const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        const nights = Math.max(0, days - 1);
        return { days, nights };
    };

    const duration = calculateDuration(startDate, endDate);

    // Format date for display
    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Generate calendar days
    const generateCalendarDays = (month) => {
        const year = month.getFullYear();
        const monthIndex = month.getMonth();
        const firstDay = new Date(year, monthIndex, 1);
        const lastDay = new Date(year, monthIndex + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Previous month days
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, monthIndex, day));
        }

        return days;
    };

    // Check if date is in range
    const isDateInRange = (date) => {
        if (!date) return false;
        const dateStr = formatDateToYYYYMMDD(date);
        
        // Hover preview - يسمح بنفس التاريخ
        if (startDate && hoverDate && !endDate && activeInput === 'end') {
            const minDate = startDate < hoverDate ? startDate : hoverDate;
            const maxDate = startDate > hoverDate ? startDate : hoverDate;
            return dateStr >= minDate && dateStr <= maxDate;
        }

        // Selected range - يسمح بنفس التاريخ
        if (startDate && endDate) {
            return dateStr >= startDate && dateStr <= endDate;
        }

        return false;
    };

    // Check if date is start or end
    const isStartDate = (date) => {
        if (!date || !startDate) return false;
        return formatDateToYYYYMMDD(date) === startDate;
    };

    const isEndDate = (date) => {
        if (!date || !endDate) return false;
        return formatDateToYYYYMMDD(date) === endDate;
    };

    // ✅ إصلاح: Handle date click بدون timezone issues
    const handleDateClick = (date) => {
        if (disabled || !date) return;

        const dateStr = formatDateToYYYYMMDD(date);

        if (activeInput === 'start') {
            onStartDateChange(dateStr);
            // Auto switch to end date
            if (endDate && new Date(dateStr) > new Date(endDate)) {
                onEndDateChange('');
            }
            // لا نغلق الـ calendar، بل نبقيه مفتوحاً للـ end date
            setActiveInput('end');
            // تحديث الـ month ليعرض التاريخ المختار
            setCurrentMonth(new Date(dateStr));
        } else if (activeInput === 'end') {
            if (startDate) {
                const endDateStr = dateStr;
                // مقارنة الـ strings لتجنب مشاكل الـ timezone
                if (endDateStr < startDate) {
                    // Swap dates
                    onEndDateChange(startDate);
                    onStartDateChange(endDateStr);
                } else {
                    // السماح باختيار نفس التاريخ (endDateStr >= startDate)
                    onEndDateChange(endDateStr);
                }
                // Close after selecting end date
                setTimeout(() => {
                    setIsOpen(false);
                    setActiveInput(null);
                }, 200);
            } else {
                onStartDateChange(dateStr);
                setActiveInput('end');
            }
        }
    };

    // Handle mouse enter (hover) - السماح بالـ hover عند اختيار نهاية التاريخ
    const handleMouseEnter = (date) => {
        if (!date || !startDate) return;
        // السماح بالـ hover حتى لو لم يتم اختيار نهاية بعد
        if (activeInput === 'end') {
            setHoverDate(formatDateToYYYYMMDD(date));
        }
    };

    const handleMouseLeave = () => {
        setHoverDate(null);
    };

    // Handle clear
    const handleClearStart = (e) => {
        e.stopPropagation();
        onStartDateChange('');
        if (endDate) {
            onEndDateChange('');
        }
    };

    const handleClearEnd = (e) => {
        e.stopPropagation();
        onEndDateChange('');
    };

    // Navigate months
    const handlePrevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    // Open calendar
    const handleOpenCalendar = (type) => {
        if (disabled) return;
        setActiveInput(type);
        setIsOpen(true);
        // Set current month based on existing dates
        if (type === 'start' && startDate) {
            setCurrentMonth(new Date(startDate));
        } else if (type === 'end' && endDate) {
            setCurrentMonth(new Date(endDate));
        }
    };

    const weekDays = isArabic
        ? ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س']
        : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const calendarDays = generateCalendarDays(currentMonth);

    return (
        <Box ref={calendarRef} sx={{ position: 'relative' }}>
            {/* Two Separate Inputs */}
            <Grid container spacing={2}>
                {/* Start Date Input */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label={isArabic ? 'تاريخ البداية' : 'Start Date'}
                        value={formatDate(startDate)}
                        onClick={() => handleOpenCalendar('start')}
                        error={error}
                        helperText={activeInput === 'start' ? helperText : ''}
                        required={required}
                        disabled={disabled}
                        placeholder={isArabic ? 'اختر التاريخ' : 'Select date'}
                        InputProps={{
                            readOnly: true,
                            startAdornment: (
                                <CalendarIcon
                                    sx={{
                                        mr: 1,
                                        color: isOpen && activeInput === 'start'
                                            ? 'primary.main'
                                            : error
                                                ? 'error.main'
                                                : 'action.active',
                                        fontSize: 18,
                                    }}
                                />
                            ),
                            endAdornment: startDate && !disabled ? (
                                <IconButton size="small" onClick={handleClearStart}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ) : null,
                            sx: {
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'background.paper',
                                '&:hover': {
                                    bgcolor: isDark
                                        ? 'rgba(255,255,255,0.08)'
                                        : alpha(theme.palette.primary.main, 0.04),
                                },
                            }
                        }}
                    />
                </Grid>

                {/* End Date Input */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        label={isArabic ? 'تاريخ النهاية' : 'End Date'}
                        value={formatDate(endDate)}
                        onClick={() => handleOpenCalendar('end')}
                        error={error}
                        helperText={activeInput === 'end' ? helperText : ''}
                        required={required}
                        disabled={disabled || !startDate}
                        placeholder={isArabic ? 'اختر التاريخ' : 'Select date'}
                        InputProps={{
                            readOnly: true,
                            startAdornment: (
                                <CalendarIcon
                                    sx={{
                                        mr: 1,
                                        color: isOpen && activeInput === 'end'
                                            ? 'secondary.main'
                                            : error
                                                ? 'error.main'
                                                : 'action.active',
                                        fontSize: 18,
                                    }}
                                />
                            ),
                            endAdornment: endDate && !disabled ? (
                                <IconButton size="small" onClick={handleClearEnd}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            ) : null,
                            sx: {
                                cursor: disabled || !startDate ? 'not-allowed' : 'pointer',
                                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'background.paper',
                                '&:hover': {
                                    bgcolor: isDark
                                        ? 'rgba(255,255,255,0.08)'
                                        : alpha(theme.palette.secondary.main, 0.04),
                                },
                            }
                        }}
                    />
                </Grid>
            </Grid>

            {/* Duration Indicator - مدمج أصغر */}
            {startDate && endDate && duration.days > 0 && (
                <Fade in>
                    <Box
                        sx={{
                            mt: 1.5,
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: isDark
                                ? alpha(theme.palette.success.main, 0.1)
                                : alpha(theme.palette.success.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <SwapIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        <Typography variant="caption" fontWeight={600} color="success.dark">
                            {duration.days} {isArabic ? 'يوم' : 'Day'}{duration.days > 1 ? (isArabic ? '' : 's') : ''}
                            {duration.nights > 0 && (
                                <>
                                    {' '} • {duration.nights} {isArabic ? (duration.nights === 1 ? 'ليلة' : 'ليالي') : (duration.nights === 1 ? 'Night' : 'Nights')}
                                </>
                            )}
                        </Typography>
                    </Box>
                </Fade>
            )}

            {/* Inline Calendar - محسّن وأصغر */}
            {isOpen && activeInput && (
                <Fade in timeout={150}>
                    <Paper
                        elevation={8}
                        sx={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            left: 0,
                            zIndex: 1400,
                            p: 2,
                            bgcolor: isDark ? '#1E293B' : '#FFFFFF',
                            border: `1.5px solid ${activeInput === 'start'
                                ? theme.palette.primary.main
                                : theme.palette.secondary.main
                                }`,
                            borderRadius: 2,
                            boxShadow: isDark
                                ? '0 12px 40px rgba(0,0,0,0.4)'
                                : '0 12px 40px rgba(0,0,0,0.12)',
                            minWidth: 280,
                            maxWidth: 320,
                        }}
                    >
                        {/* Active Input Indicator - مبسط */}
                        <Box
                            sx={{
                                mb: 1.5,
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: activeInput === 'start'
                                    ? alpha(theme.palette.primary.main, 0.1)
                                    : alpha(theme.palette.secondary.main, 0.1),
                                border: `1px solid ${activeInput === 'start'
                                    ? alpha(theme.palette.primary.main, 0.3)
                                    : alpha(theme.palette.secondary.main, 0.3)
                                    }`,
                            }}
                        >
                            <Typography
                                variant="caption"
                                fontWeight={700}
                                color={activeInput === 'start' ? 'primary.main' : 'secondary.main'}
                                textAlign="center"
                                display="block"
                            >
                                {activeInput === 'start'
                                    ? (isArabic ? 'تاريخ البداية' : 'Start Date')
                                    : (isArabic ? 'تاريخ النهاية' : 'End Date')
                                }
                            </Typography>
                            {startDate && activeInput === 'end' && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    textAlign="center"
                                    sx={{ mt: 0.3, fontSize: '0.7rem' }}
                                >
                                    {isArabic ? 'من' : 'From'} {formatDate(startDate)}
                                </Typography>
                            )}
                        </Box>

                        {/* Calendar Header - مصغر */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <IconButton
                                size="small"
                                onClick={handlePrevMonth}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                                    }
                                }}
                            >
                                <ChevronLeft fontSize="small" />
                            </IconButton>
                            <Typography variant="subtitle2" fontWeight={700} fontSize="0.875rem">
                                {currentMonth.toLocaleDateString(isArabic ? 'ar-SA' : 'en-US', {
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={handleNextMonth}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                                    }
                                }}
                            >
                                <ChevronRight fontSize="small" />
                            </IconButton>
                        </Stack>

                        {/* Week Days - مصغر */}
                        <Grid container spacing={0.3} sx={{ mb: 1 }}>
                            {weekDays.map((day, index) => (
                                <Grid item xs={12 / 7} key={index}>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            py: 0.5,
                                            fontSize: '0.7rem',
                                            fontWeight: 700,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {day}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Calendar Days - مصغر */}
                        <Grid container spacing={0.3}>
                            {calendarDays.map((date, index) => {
                                const isInRange = isDateInRange(date);
                                const isStart = isStartDate(date);
                                const isEnd = isEndDate(date);
                                const isToday = date && date.toDateString() === new Date().toDateString();
                                // السماح باختيار نفس اليوم - يتم تعطيل فقط التواريخ السابقة للبداية
                                // مقارنة الـ strings لتجنب مشاكل الـ timezone
                                const isDisabled = date && startDate && activeInput === 'end' && formatDateToYYYYMMDD(date) < startDate;

                                return (
                                    <Grid item xs={12 / 7} key={index}>
                                        {date ? (
                                            <Box
                                                onClick={() => !isDisabled && handleDateClick(date)}
                                                onMouseEnter={() => handleMouseEnter(date)}
                                                onMouseLeave={handleMouseLeave}
                                                sx={{
                                                    height: 36,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    fontSize: '0.8rem',
                                                    fontWeight: isStart || isEnd ? 700 : 500,
                                                    color: isDisabled
                                                        ? 'text.disabled'
                                                        : isStart || isEnd
                                                            ? '#FFFFFF'
                                                            : isInRange
                                                                ? activeInput === 'start' ? 'primary.main' : 'secondary.main'
                                                                : isToday
                                                                    ? 'primary.main'
                                                                    : 'text.primary',
                                                    bgcolor: isStart
                                                        ? 'primary.main'
                                                        : isEnd
                                                            ? 'secondary.main'
                                                            : isInRange
                                                                ? alpha(activeInput === 'start' ? theme.palette.primary.main : theme.palette.secondary.main, 0.12)
                                                                : 'transparent',
                                                    borderRadius: isStart || isEnd ? '50%' : 0.8,
                                                    border: isToday && !isStart && !isEnd
                                                        ? `1.5px solid ${theme.palette.primary.main}`
                                                        : 'none',
                                                    opacity: isDisabled ? 0.3 : 1,
                                                    userSelect: 'none',
                                                    transition: 'all 0.15s ease',
                                                    '&:hover': !isDisabled ? {
                                                        transform: 'scale(1.08)',
                                                        bgcolor: isStart || isEnd
                                                            ? isStart ? 'primary.dark' : 'secondary.dark'
                                                            : alpha(activeInput === 'start' ? theme.palette.primary.main : theme.palette.secondary.main, 0.2),
                                                        boxShadow: 1,
                                                    } : {},
                                                }}
                                            >
                                                {date.getDate()}
                                            </Box>
                                        ) : (
                                            <Box sx={{ height: 36 }} />
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {/* Range Preview - مصغر */}
                        {startDate && hoverDate && activeInput === 'end' && (
                            <Fade in>
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        p: 1,
                                        borderRadius: 1.5,
                                        bgcolor: alpha(theme.palette.info.main, 0.08),
                                        border: `1px dashed ${alpha(theme.palette.info.main, 0.4)}`,
                                        textAlign: 'center',
                                    }}
                                >
                                    {(() => {
                                        const dur = calculateDuration(startDate, hoverDate);
                                        return (
                                            <Typography variant="caption" color="info.main" fontWeight={600} fontSize="0.75rem">
                                                {dur.days} {isArabic ? 'يوم' : 'Day'}{dur.days > 1 ? (isArabic ? '' : 's') : ''} 
                                                {dur.nights > 0 && (
                                                    <>
                                                        • {dur.nights} {isArabic ? (dur.nights === 1 ? 'ليلة' : 'ليالي') : (dur.nights === 1 ? 'Night' : 'Nights')}
                                                    </>
                                                )}
                                            </Typography>
                                        );
                                    })()}
                                </Box>
                            </Fade>
                        )}
                    </Paper>
                </Fade>
            )}
        </Box>
    );
};

export default DateRangePicker;
